import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Info, Minus, Plus, Search, ShieldCheck, Undo2, Users } from 'lucide-react';
import {
  STUDIO_OPTIONS, deleteMailUserRole, listBenutzerUebersicht, searchUsers, setzeRechte, updateMailUserStudios, upsertMailUserRole,
  type BenutzerZeile, type MailRole, type UserSearchHit,
} from '../../lib/supabase';
import { formatDateTime, roleLabel } from '../../lib/format';
import { sportschulenFuerStudios } from '../../lib/sportschulen';
import { useAuthStore } from '../../stores/authStore';
import { Modal } from '../../components/Modal/Modal';
import { useToast } from '../../components/Toast';
import './RechteTab.css';

type Filter = 'alle' | MailRole | 'ohne-freigabe' | 'freigeben';

const ROLLEN: { id: MailRole; label: string }[] = [
  { id: 'admin', label: 'Administrator' },
  { id: 'redakteur', label: 'Redaktion' },
  { id: 'geschaeftsfuehrung', label: 'Geschäftsführung' },
  { id: 'studio_leiter', label: 'Studioleitung' },
  { id: 'viewer', label: 'Nur lesen' },
];

/** Wer darf was — die Aufschlüsselung oben im Reiter. */
const MATRIX: { recht: string; werte: Record<MailRole, string> }[] = [
  { recht: 'Mails im Assistenten vorbereiten und testen', werte: { admin: 'ja', redakteur: 'ja', geschaeftsfuehrung: 'ja', studio_leiter: 'eigene Sportschulen', viewer: '—' } },
  { recht: 'Zur Freigabe einreichen', werte: { admin: 'ja', redakteur: 'ja', geschaeftsfuehrung: 'ja', studio_leiter: 'ja', viewer: '—' } },
  { recht: 'Ohne Freigabe versenden (nach dem Versand-Check)', werte: { admin: 'immer', redakteur: 'mit Recht', geschaeftsfuehrung: 'mit Recht', studio_leiter: 'mit Recht', viewer: '—' } },
  { recht: 'Eingereichte Mails freigeben, zurückfragen, ablehnen', werte: { admin: 'immer', redakteur: 'mit Freigaberecht', geschaeftsfuehrung: '—', studio_leiter: '—', viewer: '—' } },
  { recht: 'Kampagnen-Editor, Gruppen, Kontakte, Abmeldungen', werte: { admin: 'ja', redakteur: 'ja', geschaeftsfuehrung: '—', studio_leiter: '—', viewer: 'nur ansehen' } },
  { recht: 'Einstellungen, Benutzer & Rechte, Datenqualität', werte: { admin: 'ja', redakteur: '—', geschaeftsfuehrung: '—', studio_leiter: '—', viewer: '—' } },
];

function wert(w: string) {
  if (w === '—') return <span className="rt-nein" aria-label="nein"><Minus size={14} aria-hidden="true" /></span>;
  if (w === 'ja' || w === 'immer') return <span className="rt-ja"><Check size={14} aria-hidden="true" /> {w === 'immer' ? 'immer' : ''}</span>;
  return <span className="rt-teil">{w}</span>;
}

function studiosText(z: BenutzerZeile): string {
  if (z.rolle === 'studio_leiter') {
    const s = sportschulenFuerStudios(z.studios).map((x) => x.name);
    return s.length ? s.join(', ') : 'keine zugeordnet';
  }
  if (z.rolle === 'geschaeftsfuehrung' || z.rolle === 'admin' || z.rolle === 'redakteur') return 'alle';
  return '—';
}

/**
 * Benutzer & Rechte: alle, die das Mailtool nutzen können — mit Eintrag im
 * Mailtool oder über ihre CRM-Rolle —, samt Rolle, Sportschulen und Rechten.
 * Hier vergibt ein Admin auch „ohne Freigabe versenden", seit 0042 auch an
 * Geschäftsführung und Studioleitung.
 */
export function RechteTab() {
  const qc = useQueryClient();
  const toast = useToast();
  const ich = useAuthStore((s) => s.user);
  const liste = useQuery({ queryKey: ['benutzer-uebersicht'], queryFn: listBenutzerUebersicht });
  const [suche, setSuche] = useState('');
  const [filter, setFilter] = useState<Filter>('alle');
  const [neu, setNeu] = useState(false);
  const [studiosFuer, setStudiosFuer] = useState<BenutzerZeile | null>(null);

  const neuLaden = () => {
    void qc.invalidateQueries({ queryKey: ['benutzer-uebersicht'] });
    void qc.invalidateQueries({ queryKey: ['mail-users'] });
    void qc.invalidateQueries({ queryKey: ['redaktion'] });
  };

  const rechte = useMutation({
    mutationFn: ({ z, teil }: { z: BenutzerZeile; teil: Parameters<typeof setzeRechte>[1] }) => setzeRechte(z, teil),
    onSuccess: () => { neuLaden(); toast.success('Gespeichert.'); },
    onError: (e) => toast.error('Nicht gespeichert: ' + (e as Error).message),
  });
  const entfernen = useMutation({
    mutationFn: (z: BenutzerZeile) => deleteMailUserRole(z.user_id),
    onSuccess: () => { neuLaden(); toast.success('Eintrag entfernt.'); },
    onError: (e) => toast.error('Nicht entfernt: ' + (e as Error).message),
  });

  const alle = useMemo(() => liste.data ?? [], [liste.data]);
  const zahl = (f: Filter) => alle.filter((z) => passt(z, f)).length;
  const sichtbar = useMemo(() => {
    const q = suche.trim().toLowerCase();
    return alle.filter((z) => passt(z, filter) && (!q || `${z.name ?? ''} ${z.email ?? ''}`.toLowerCase().includes(q)));
  }, [alle, filter, suche]);

  return (
    <div className="rt">
      <div className="alert alert-info" style={{ marginBottom: '1rem' }}>
        <Info size={16} aria-hidden="true" />
        <div>
          <strong>Wer das Mailtool nutzt.</strong> Geschäftsführung und Studioleitung bekommen ihre Rolle von selbst aus dem CRM
          („aus dem CRM"). Ein Eintrag hier geht vor — er entsteht, sobald Du ein Recht vergibst, und verschwindet wieder, wenn
          alles wie im CRM ist. Neue Leute sind erst zu finden, wenn sie sich einmal mit Google angemeldet haben.
        </div>
      </div>

      <div className="rt-zahlen" data-tour="rechte-zahlen">
        {ROLLEN.map((r) => (
          <button key={r.id} type="button" className={`rt-zahl${filter === r.id ? ' aktiv' : ''}`} onClick={() => setFilter(filter === r.id ? 'alle' : r.id)} aria-pressed={filter === r.id}>
            <span className="rt-zahl-wert">{zahl(r.id)}</span>
            <span className="rt-zahl-label">{r.label}</span>
          </button>
        ))}
        <button type="button" className={`rt-zahl rt-zahl--recht${filter === 'freigeben' ? ' aktiv' : ''}`} onClick={() => setFilter(filter === 'freigeben' ? 'alle' : 'freigeben')} aria-pressed={filter === 'freigeben'}>
          <span className="rt-zahl-wert">{zahl('freigeben')}</span>
          <span className="rt-zahl-label">geben frei</span>
        </button>
        <button type="button" className={`rt-zahl rt-zahl--recht${filter === 'ohne-freigabe' ? ' aktiv' : ''}`} onClick={() => setFilter(filter === 'ohne-freigabe' ? 'alle' : 'ohne-freigabe')} aria-pressed={filter === 'ohne-freigabe'}>
          <span className="rt-zahl-wert">{zahl('ohne-freigabe')}</span>
          <span className="rt-zahl-label">versenden ohne Freigabe</span>
        </button>
      </div>

      <details className="card rt-matrix" open data-tour="rechte-matrix">
        <summary><ShieldCheck size={16} aria-hidden="true" /> Wer darf was?</summary>
        <div className="rt-tabelle-rahmen">
          <table className="data-table rt-matrix-tabelle">
            <thead>
              <tr><th scope="col">Recht</th>{ROLLEN.map((r) => <th key={r.id} scope="col">{r.label}</th>)}</tr>
            </thead>
            <tbody>
              {MATRIX.map((m) => (
                <tr key={m.recht}>
                  <th scope="row">{m.recht}</th>
                  {ROLLEN.map((r) => <td key={r.id}>{wert(m.werte[r.id])}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="form-hint" style={{ margin: '0.75rem 0 0' }}>
          „Ohne Freigabe versenden" heißt: Die Person verschickt ihre eigenen Mails nach dem Versand-Check selbst, ohne dass die
          Redaktion vorher draufschaut. Gut für alle, bei denen es sich eingespielt hat. Im Verlauf der Mail steht es trotzdem.
        </p>
      </details>

      <div className="rt-leiste">
        <div className="rt-suche">
          <Search size={15} aria-hidden="true" />
          <input className="form-input" placeholder="Name oder E-Mail suchen" value={suche} onChange={(e) => setSuche(e.target.value)} aria-label="Person suchen" />
        </div>
        {filter !== 'alle' && <button type="button" className="btn btn-ghost btn-sm" onClick={() => setFilter('alle')}>Filter zurücksetzen</button>}
        <span style={{ flex: 1 }} />
        <button type="button" className="btn btn-primary" onClick={() => setNeu(true)} data-tour="rechte-hinzufuegen"><Plus size={16} aria-hidden="true" /> Person hinzufügen</button>
      </div>

      {liste.isLoading ? (
        <div className="loading-spinner" style={{ margin: '2rem auto' }} />
      ) : liste.isError ? (
        <div className="alert alert-danger"><Info size={16} aria-hidden="true" /><div>{(liste.error as Error).message}</div></div>
      ) : sichtbar.length === 0 ? (
        <div className="alert alert-info"><Users size={16} aria-hidden="true" /><div>Niemand passt zu Suche und Filter.</div></div>
      ) : (
        <div className="rt-tabelle-rahmen" data-tour="rechte-tabelle">
          <table className="data-table rt-tabelle">
            <thead>
              <tr>
                <th scope="col">Person</th>
                <th scope="col">Rolle</th>
                <th scope="col">Sportschulen</th>
                <th scope="col">Freigaberecht</th>
                <th scope="col">Ohne Freigabe versenden</th>
                <th scope="col">Zuletzt angemeldet</th>
                <th scope="col">Mails</th>
                <th scope="col"><span className="sr-only">Aktionen</span></th>
              </tr>
            </thead>
            <tbody>
              {sichtbar.map((z) => {
                const selbst = z.user_id === ich?.id;
                const beschaeftigt = rechte.isPending && rechte.variables?.z.user_id === z.user_id;
                return (
                  <tr key={z.user_id}>
                    <td>
                      <div className="rt-name">{z.name || z.email}</div>
                      <div className="rt-mail">{z.email}</div>
                    </td>
                    <td>
                      <select className="form-select rt-rolle" value={z.rolle} disabled={selbst || beschaeftigt}
                        aria-label={`Rolle von ${z.name ?? z.email}`}
                        title={selbst ? 'Die eigene Rolle ändert ein anderer Admin.' : undefined}
                        onChange={(e) => rechte.mutate({ z, teil: { rolle: e.target.value as MailRole } })}>
                        {ROLLEN.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
                      </select>
                      <div className="rt-quelle">
                        {z.quelle === 'crm'
                          ? <span className="badge badge-info">aus dem CRM</span>
                          : z.crm_rolle && z.crm_rolle !== z.rolle
                            ? <span className="badge badge-muted" title="Der Eintrag im Mailtool geht vor">im CRM: {roleLabel(z.crm_rolle)}</span>
                            : <span className="badge badge-muted">im Mailtool eingetragen</span>}
                      </div>
                    </td>
                    <td className="rt-studios">
                      {studiosText(z)}
                      {z.rolle === 'studio_leiter' && (
                        <button type="button" className="zf-aendern" onClick={() => setStudiosFuer(z)}>ändern</button>
                      )}
                    </td>
                    <td>
                      {z.rolle === 'admin' ? wert('immer')
                        : z.rolle === 'redakteur' ? (
                          <label className="rt-schalter">
                            <input type="checkbox" checked={z.darf_freigeben} disabled={beschaeftigt}
                              onChange={(e) => rechte.mutate({ z, teil: { freigeben: e.target.checked } })} />
                            <span>darf freigeben</span>
                          </label>
                        ) : wert('—')}
                    </td>
                    <td>
                      {z.rolle === 'admin' ? wert('immer')
                        : z.rolle === 'viewer' ? wert('—') : (
                          <label className="rt-schalter">
                            <input type="checkbox" checked={z.darf_ohne_freigabe} disabled={beschaeftigt}
                              onChange={(e) => {
                                if (e.target.checked && !confirm(`${z.name ?? z.email} verschickt eigene Mails dann nach dem Versand-Check selbst — ohne Freigabe der Redaktion. Erlauben?`)) return;
                                rechte.mutate({ z, teil: { ohneFreigabe: e.target.checked } });
                              }} />
                            <span>erlaubt</span>
                          </label>
                        )}
                    </td>
                    <td className="rt-klein">{z.letzte_anmeldung ? formatDateTime(z.letzte_anmeldung) : 'noch nie'}</td>
                    <td className="rt-klein">{z.mails}{z.mails_offen > 0 && <> · <span className="rt-offen">{z.mails_offen} offen</span></>}</td>
                    <td style={{ textAlign: 'right' }}>
                      {z.quelle === 'mailtool' && !selbst && (
                        <button type="button" className="btn btn-ghost btn-sm" disabled={entfernen.isPending}
                          title={z.crm_rolle ? 'Eintrag entfernen — dann gilt wieder die Rolle aus dem CRM' : 'Eintrag entfernen — dann hat die Person keinen Zugang mehr'}
                          onClick={() => {
                            const text = z.crm_rolle
                              ? `Eintrag für ${z.name ?? z.email} entfernen? Danach gilt wieder die CRM-Rolle (${roleLabel(z.crm_rolle)}).`
                              : `Eintrag für ${z.name ?? z.email} entfernen? Danach hat die Person keinen Zugang mehr zum Mailtool.`;
                            if (confirm(text)) entfernen.mutate(z);
                          }}>
                          <Undo2 size={14} aria-hidden="true" /> {z.crm_rolle ? 'CRM-Rolle' : 'Entfernen'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <PersonHinzufuegen offen={neu} onSchliessen={() => setNeu(false)} onFertig={() => { setNeu(false); neuLaden(); }} />
      {studiosFuer && (
        <StudiosAendern z={studiosFuer} onSchliessen={() => setStudiosFuer(null)} onFertig={() => { setStudiosFuer(null); neuLaden(); }} />
      )}
    </div>
  );
}

function passt(z: BenutzerZeile, f: Filter): boolean {
  if (f === 'alle') return true;
  if (f === 'ohne-freigabe') return z.darf_ohne_freigabe;
  if (f === 'freigeben') return z.darf_freigeben;
  return z.rolle === f;
}

function PersonHinzufuegen({ offen, onSchliessen, onFertig }: { offen: boolean; onSchliessen: () => void; onFertig: () => void }) {
  const toast = useToast();
  const [person, setPerson] = useState<UserSearchHit | null>(null);
  const [rolle, setRolle] = useState<MailRole>('redakteur');
  const speichern = useMutation({
    mutationFn: () => upsertMailUserRole(person!.user_id, rolle, person!.name ?? ''),
    onSuccess: () => { toast.success('Hinzugefügt.'); setPerson(null); onFertig(); },
    onError: (e) => toast.error('Nicht gespeichert: ' + (e as Error).message),
  });
  return (
    <Modal isOpen={offen} onClose={onSchliessen} title="Person hinzufügen">
      <p className="form-hint" style={{ marginTop: 0 }}>
        Geschäftsführung und Studioleitung brauchen keinen Eintrag — sie kommen über das CRM. Hier trägst Du alle anderen ein,
        etwa die Redaktion. Die Person muss sich vorher einmal mit Google angemeldet haben.
      </p>
      <PersonSuche onWahl={setPerson} />
      {person && (
        <p className="rt-gewaehlt"><strong>{person.name ?? person.email}</strong> · {person.email}</p>
      )}
      <div className="form-group">
        <label className="form-label" htmlFor="neue-rolle">Rolle</label>
        <select id="neue-rolle" className="form-select" value={rolle} onChange={(e) => setRolle(e.target.value as MailRole)}>
          <option value="admin">Administrator — alles, gibt frei, versendet selbst</option>
          <option value="redakteur">Redaktion — Kampagnen, Gruppen, Kontakte; Freigaberecht einzeln</option>
          <option value="geschaeftsfuehrung">Geschäftsführung — bereitet Mails vor, Freigabe nötig</option>
          <option value="studio_leiter">Studioleitung — Mails für die eigenen Sportschulen, Freigabe nötig</option>
          <option value="viewer">Nur lesen</option>
        </select>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1.25rem' }}>
        <button type="button" className="btn btn-secondary" onClick={onSchliessen}>Abbrechen</button>
        <button type="button" className="btn btn-primary" onClick={() => speichern.mutate()} disabled={!person || speichern.isPending}>Hinzufügen</button>
      </div>
    </Modal>
  );
}

function StudiosAendern({ z, onSchliessen, onFertig }: { z: BenutzerZeile; onSchliessen: () => void; onFertig: () => void }) {
  const toast = useToast();
  const [studios, setStudios] = useState<string[]>(z.eigene_studios ? z.studios : []);
  const speichern = useMutation({
    mutationFn: async () => {
      if (z.quelle === 'crm') await upsertMailUserRole(z.user_id, 'studio_leiter', z.name ?? '', studios);
      else await updateMailUserStudios(z.user_id, studios);
    },
    onSuccess: () => { toast.success('Sportschulen gespeichert.'); onFertig(); },
    onError: (e) => toast.error('Nicht gespeichert: ' + (e as Error).message),
  });
  return (
    <Modal isOpen onClose={onSchliessen} title={`Sportschulen für ${z.name ?? z.email}`}>
      <p className="form-hint" style={{ marginTop: 0 }}>
        Ohne Auswahl gelten die Sportschulen aus dem CRM. Mit Auswahl gelten nur diese — die Studioleitung sieht dann genau deren Gruppen.
      </p>
      <div className="rt-studio-wahl">
        {STUDIO_OPTIONS.map((s) => {
          const an = studios.includes(s);
          return (
            <label key={s} className={`chip${an ? ' active' : ''}`}>
              <input type="checkbox" checked={an} onChange={() => setStudios(an ? studios.filter((x) => x !== s) : [...studios, s])} /> {s}
            </label>
          );
        })}
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1.25rem' }}>
        <button type="button" className="btn btn-secondary" onClick={onSchliessen}>Abbrechen</button>
        <button type="button" className="btn btn-primary" onClick={() => speichern.mutate()} disabled={speichern.isPending}>Speichern</button>
      </div>
    </Modal>
  );
}

function PersonSuche({ onWahl }: { onWahl: (t: UserSearchHit) => void }) {
  const [q, setQ] = useState('');
  const suche = useQuery({
    queryKey: ['user-search', q.trim()],
    queryFn: () => searchUsers(q.trim()),
    enabled: q.trim().length >= 2,
  });
  return (
    <div className="form-group">
      <label className="form-label" htmlFor="person-suche">Person suchen</label>
      <input id="person-suche" className="form-input" placeholder="Name oder E-Mail, mindestens 2 Zeichen" value={q} onChange={(e) => setQ(e.target.value)} />
      {suche.isError && <p className="form-hint" style={{ color: 'var(--danger-dark)' }}>{(suche.error as Error).message}</p>}
      {suche.data && suche.data.length === 0 && <p className="form-hint">Niemand gefunden — hat sich die Person schon einmal angemeldet?</p>}
      {suche.data && suche.data.length > 0 && (
        <ul className="rt-treffer">
          {suche.data.slice(0, 8).map((t) => (
            <li key={t.user_id}>
              <button type="button" className="btn btn-ghost" onClick={() => onWahl(t)}>
                <span>{t.name ?? t.email} <span className="rt-mail">· {t.email}</span></span>
                <span className="rt-mail">{t.mail_rolle ? `Mailtool: ${roleLabel(t.mail_rolle)}` : t.crm_rolle ? `CRM: ${t.crm_rolle}` : ''}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
